import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  BarList,
  ColumnChart,
  DataTable,
  EmptyState,
  SectionCard,
  StatTile,
  TargetBar,
  fmtDate,
  fmtMoney,
  fmtNum,
  fmtPct,
  type Column,
} from "@/components/reports/ReportWidgets";
import { buildQuery, filterSelectClass, useReport, type DateRange } from "@/components/reports/DateRangeFilter";
import { ListPager } from "@/components/ui/list-pager";
import type { ExecRow, ForecastBucket, HeadRow, LeadSourceRow, StageRow, UserMetrics } from "@/types/report";
import { DonutPieChart } from "@/components/charts/DonutPieChart";
import { AreaTrendChart } from "@/components/charts/AreaTrendChart";
import { FunnelConversionChart } from "@/components/charts/FunnelConversionChart";
import { RadialTargetGauge } from "@/components/charts/RadialTargetGauge";
import { GroupedBarChart } from "@/components/charts/GroupedBarChart";

export interface TabProps {
  range: DateRange;
  headId: string;
}

export function Status({ loading, error, children }: { loading: boolean; error: string | null; children: React.ReactNode }) {
  if (error) return <p className="text-sm text-destructive py-6 text-center">{error}</p>;
  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-10 bg-background/40">
          <Loader2 className="size-5 animate-spin text-primary" />
        </div>
      )}
      {children}
    </div>
  );
}

export const q = (range: DateRange, headId: string, extra: Record<string, string | number> = {}) =>
  buildQuery({ from: range.from, to: range.to, head_id: headId, ...extra });

// ---------------------------------------------------------------------------
// Sales Executives
// ---------------------------------------------------------------------------

export function ExecTab({ range, headId }: TabProps) {
  const navigate = useNavigate();
  const { data, loading, error } = useReport<{ rows: ExecRow[]; totals: UserMetrics }>(`/api/Report/ExecPerformance${q(range, headId)}`);
  const rows = data?.rows || [];

  const columns: Column<ExecRow>[] = [
    { key: "name", header: "Executive", render: (r) => <span className="font-medium text-foreground">{r.name}{!r.active && <span className="text-muted-foreground"> (inactive)</span>}</span> },
    { key: "sales_head", header: "Sales Head", render: (r) => r.sales_head || "—" },
    { key: "leads_assigned", header: "Leads", align: "right" },
    { key: "leads_converted", header: "Converted", align: "right" },
    { key: "conversion_pct", header: "Conv. %", align: "right", render: (r) => fmtPct(r.conversion_pct) },
    { key: "leads_untouched", header: "Untouched", align: "right" },
    { key: "deals_won", header: "Won", align: "right" },
    { key: "deals_lost", header: "Lost", align: "right" },
    { key: "won_amount", header: "Revenue", align: "right", render: (r) => fmtMoney(r.won_amount) },
    { key: "win_rate_pct", header: "Win rate", align: "right", render: (r) => fmtPct(r.win_rate_pct) },
    { key: "avg_deal_size", header: "Avg deal", align: "right", render: (r) => fmtMoney(r.avg_deal_size) },
    { key: "avg_cycle_days", header: "Cycle (days)", align: "right" },
    { key: "pipeline_amount", header: "Pipeline", align: "right", render: (r) => fmtMoney(r.pipeline_amount) },
    { key: "quotes_sent", header: "Quotes sent", align: "right" },
    { key: "quotes_accepted", header: "Accepted", align: "right" },
    { key: "avg_discount_pct", header: "Avg disc.", align: "right", render: (r) => fmtPct(r.avg_discount_pct) },
    { key: "calls", header: "Calls", align: "right" },
    { key: "meetings", header: "Meetings", align: "right" },
    { key: "tasks_done", header: "Tasks done", align: "right" },
    { key: "tasks_overdue", header: "Overdue", align: "right" },
    {
      key: "target_achieved_pct",
      header: "Target",
      sortValue: (r) => r.target_achieved_pct ?? -1,
      render: (r) => <TargetBar achieved={r.won_amount} target={r.target_amount} compact />,
    },
  ];

  const t = data?.totals;
  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        {t && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile label="Revenue won" value={fmtMoney(t.won_amount)} hint={<span>{t.deals_won} deals</span>} />
            <StatTile label="Lead conversion" value={fmtPct(t.conversion_pct)} hint={<span>{t.leads_converted} of {t.leads_assigned} leads</span>} />
            <StatTile label="Win rate" value={fmtPct(t.win_rate_pct)} hint={<span>{t.deals_won} won · {t.deals_lost} lost</span>} />
            <StatTile label="Open pipeline" value={fmtMoney(t.pipeline_amount)} hint={<span>{t.open_deals} deals</span>} />
          </div>
        )}
        <SectionCard title="Sales Executive performance" description="Click a row to see that executive's leads, deals and activity">
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.user_id}
            onRowClick={(r) => navigate(`/reports/user/${r.user_id}`)}
            defaultSort={{ key: "won_amount", dir: "desc" }}
            emptyMessage="No Sales Executives in this view."
          />
        </SectionCard>
        {rows.length > 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Revenue & Pipeline comparison" description="Won deal value vs active pipeline per executive">
              <GroupedBarChart
                data={rows.slice(0, 10).map((r) => ({
                  category: r.name,
                  won: r.won_amount,
                  pipeline: r.pipeline_amount,
                }))}
                series={[
                  { key: "won", name: "Won Revenue", color: "#10b981" },
                  { key: "pipeline", name: "Open Pipeline", color: "#3b82f6" },
                ]}
                formatValue={(v) => fmtMoney(v)}
                height={220}
              />
            </SectionCard>

            <SectionCard title="Revenue contribution" description="Share of total won revenue">
              <DonutPieChart
                data={rows.filter((r) => r.won_amount > 0).map((r) => ({
                  label: r.name,
                  value: r.won_amount,
                  sublabel: `${r.deals_won} deals won`,
                }))}
                formatValue={(v) => fmtMoney(v)}
                centerValue={t ? fmtMoney(t.won_amount) : undefined}
                centerLabel="Total Won"
                donut={true}
                onSliceClick={(slice) => {
                  const match = rows.find((r) => r.name === slice.label);
                  if (match) navigate(`/reports/user/${match.user_id}`);
                }}
              />
            </SectionCard>
          </div>
        )}
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Sales Heads (Super Admin)
// ---------------------------------------------------------------------------

export function HeadTab({ range, onPickHead }: TabProps & { onPickHead: (id: string) => void }) {
  const navigate = useNavigate();
  const { data, loading, error } = useReport<{ rows: HeadRow[] }>(`/api/Report/HeadPerformance${q(range, "")}`);
  const rows = data?.rows || [];

  const columns: Column<HeadRow>[] = [
    { key: "name", header: "Sales Head", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "team_size", header: "Team size", align: "right" },
    { key: "leads_assigned", header: "Leads", align: "right" },
    { key: "conversion_pct", header: "Conv. %", align: "right", render: (r) => fmtPct(r.conversion_pct) },
    { key: "won_amount", header: "Revenue", align: "right", render: (r) => fmtMoney(r.won_amount) },
    { key: "win_rate_pct", header: "Win rate", align: "right", render: (r) => fmtPct(r.win_rate_pct) },
    { key: "pipeline_amount", header: "Pipeline", align: "right", render: (r) => fmtMoney(r.pipeline_amount) },
    { key: "quotes_pending_approval", header: "Pending approvals", align: "right" },
    { key: "avg_approval_hours", header: "Avg approval (h)", align: "right" },
    { key: "tasks_overdue", header: "Overdue tasks", align: "right" },
    {
      key: "target_achieved_pct",
      header: "Team target",
      sortValue: (r) => r.target_achieved_pct ?? -1,
      render: (r) => <TargetBar achieved={r.won_amount} target={r.target_amount} compact />,
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <span className="flex gap-3">
          <button type="button" className="text-primary hover:underline" onClick={(e) => { e.stopPropagation(); onPickHead(r.user_id); }}>
            Team
          </button>
          <button type="button" className="text-primary hover:underline" onClick={(e) => { e.stopPropagation(); navigate(`/reports/user/${r.user_id}`); }}>
            Details
          </button>
        </span>
      ),
    },
  ];

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <SectionCard title="Sales Head performance" description="Totals for each head and everyone reporting to them. Team target = the head's own target, or the sum of their executives' targets.">
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.user_id}
            onRowClick={(r) => onPickHead(r.user_id)}
            defaultSort={{ key: "won_amount", dir: "desc" }}
            emptyMessage="No Sales Heads yet."
          />
        </SectionCard>
        {rows.length > 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Team comparison" description="Revenue, target and open pipeline per Sales Head">
              <GroupedBarChart
                data={rows.map((r) => ({
                  category: r.name,
                  revenue: r.won_amount,
                  target: r.target_amount,
                  pipeline: r.pipeline_amount,
                }))}
                series={[
                  { key: "revenue", name: "Won Revenue", color: "#10b981" },
                  { key: "target", name: "Target Quota", color: "#64748b" },
                  { key: "pipeline", name: "Open Pipeline", color: "#3b82f6" },
                ]}
                formatValue={(v) => fmtMoney(v)}
                height={220}
              />
            </SectionCard>

            <SectionCard title="Revenue share by team" description="Percentage contribution of each sales team">
              <DonutPieChart
                data={rows.filter((r) => r.won_amount > 0).map((r) => ({
                  label: r.name,
                  value: r.won_amount,
                  sublabel: `${r.team_size} execs`,
                }))}
                formatValue={(v) => fmtMoney(v)}
                centerValue={fmtMoney(rows.reduce((sum, r) => sum + r.won_amount, 0))}
                centerLabel="Total Revenue"
                donut={true}
                onSliceClick={(slice) => {
                  const match = rows.find((r) => r.name === slice.label);
                  if (match) onPickHead(match.user_id);
                }}
              />
            </SectionCard>
          </div>
        )}
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Pipeline
// ---------------------------------------------------------------------------

export function PipelineTab({ headId }: TabProps) {
  const navigate = useNavigate();
  const { data, loading, error } = useReport<{
    stages: StageRow[];
    ageing: Array<{ bucket: string; deals: number; amount: number }>;
    stale_deals: Array<{ id: string; name: string; amount: number; stage_name: string; owner_name: string; idle_days: number }>;
  }>(`/api/Report/Pipeline${buildQuery({ head_id: headId })}`);

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Open deals by stage" description="Distribution of active deal value">
            <DonutPieChart
              data={(data?.stages || []).map((s) => ({
                label: s.stage_name,
                value: s.amount,
                sublabel: `${s.deals} deals`,
              }))}
              formatValue={(v) => fmtMoney(v)}
              centerValue={fmtMoney((data?.stages || []).reduce((sum, s) => sum + s.amount, 0))}
              centerLabel="Pipeline"
              donut={true}
              onSliceClick={() => navigate("/deals")}
            />
          </SectionCard>

          <SectionCard title="Pipeline conversion funnel" description="Stage-by-stage deal volume and drop-off">
            <FunnelConversionChart
              stages={(data?.stages || []).map((s) => ({
                name: s.stage_name,
                count: s.deals,
                amount: s.amount,
              }))}
              formatMoney={(v) => fmtMoney(v)}
              onStageClick={() => navigate("/deals")}
            />
          </SectionCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SectionCard title="Deal age" description="Open deals by days since created">
            <BarList
              data={(data?.ageing || []).map((a) => ({ label: a.bucket, value: a.deals, detail: [fmtMoney(a.amount)] }))}
              emptyMessage="No open deals."
            />
          </SectionCard>
          <SectionCard title="Stale deals" description="Open deals with no activity for 14+ days" className="lg:col-span-2">
            <DataTable
              columns={[
                { key: "name", header: "Deal", render: (r) => <Link to={`/deals/${r.id}`} className="font-medium text-primary hover:underline">{r.name}</Link> },
                { key: "stage_name", header: "Stage" },
                { key: "owner_name", header: "Owner" },
                { key: "amount", header: "Amount", align: "right", render: (r) => fmtMoney(r.amount) },
                { key: "idle_days", header: "Idle days", align: "right" },
              ]}
              rows={data?.stale_deals || []}
              rowKey={(r) => r.id}
              defaultSort={{ key: "idle_days", dir: "desc" }}
              emptyMessage="No stale deals."
            />
          </SectionCard>
        </div>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Lead sources
// ---------------------------------------------------------------------------

export function SourcesTab({ range, headId }: TabProps) {
  const { data, loading, error } = useReport<{ rows: LeadSourceRow[] }>(`/api/Report/LeadSource${q(range, headId)}`);
  const rows = data?.rows || [];
  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Leads by source" description="Lead generation volume per channel">
            <DonutPieChart
              data={rows.filter((r) => r.leads > 0).map((r) => ({
                label: r.source_name,
                value: r.leads,
                sublabel: `${fmtPct(r.conversion_pct)} converted`,
              }))}
              donut={true}
              centerValue={String(rows.reduce((sum, r) => sum + r.leads, 0))}
              centerLabel="Total Leads"
            />
          </SectionCard>
          <SectionCard title="Revenue by source" description="Won deals originating from each channel">
            <DonutPieChart
              data={rows.filter((r) => r.won_amount > 0).map((r) => ({
                label: r.source_name,
                value: r.won_amount,
                sublabel: `${r.converted} converted`,
              }))}
              formatValue={(v) => fmtMoney(v)}
              centerValue={fmtMoney(rows.reduce((sum, r) => sum + r.won_amount, 0))}
              centerLabel="Won Revenue"
              donut={true}
            />
          </SectionCard>
        </div>
        <SectionCard title="Source details">
          <DataTable
            columns={[
              { key: "source_name", header: "Source", render: (r) => <span className="font-medium text-foreground">{r.source_name}</span> },
              { key: "leads", header: "Leads", align: "right" },
              { key: "converted", header: "Converted", align: "right" },
              { key: "disqualified", header: "Disqualified", align: "right" },
              { key: "conversion_pct", header: "Conv. %", align: "right", render: (r) => fmtPct(r.conversion_pct) },
              { key: "won_amount", header: "Revenue", align: "right", render: (r) => fmtMoney(r.won_amount) },
            ]}
            rows={rows}
            rowKey={(r) => r.source}
            defaultSort={{ key: "leads", dir: "desc" }}
            emptyMessage="No leads in this period."
          />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Lost analysis
// ---------------------------------------------------------------------------

export function LostTab({ range, headId }: TabProps) {
  const { data, loading, error } = useReport<{
    lead_reasons: Array<{ reason: string; count: number }>;
    deal_reasons: Array<{ reason: string; count: number; amount: number }>;
  }>(`/api/Report/LostAnalysis${q(range, headId)}`);
  return (
    <Status loading={loading} error={error}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Why deals were lost" description="Lost deal value breakdown by reason">
          <DonutPieChart
            data={(data?.deal_reasons || []).filter((r) => r.amount > 0).map((r) => ({
              label: r.reason,
              value: r.amount,
              sublabel: `${r.count} deals`,
            }))}
            formatValue={(v) => fmtMoney(v)}
            centerValue={fmtMoney((data?.deal_reasons || []).reduce((sum, r) => sum + r.amount, 0))}
            centerLabel="Lost Value"
            donut={true}
          />
        </SectionCard>
        <SectionCard title="Why leads were disqualified" description="Disqualification volume by reason">
          <DonutPieChart
            data={(data?.lead_reasons || []).filter((r) => r.count > 0).map((r) => ({
              label: r.reason,
              value: r.count,
            }))}
            centerValue={String((data?.lead_reasons || []).reduce((sum, r) => sum + r.count, 0))}
            centerLabel="Disqualified"
            donut={true}
          />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Lead ageing
// ---------------------------------------------------------------------------

export function AgeingTab({ headId }: TabProps) {
  const [days, setDays] = useState(7);
  const { data, loading, error } = useReport<{
    rows: Array<{ lead_status: string; d0_3: number; d4_7: number; d8_14: number; d15_30: number; d30_plus: number; total: number }>;
    stuck_leads: Array<{ id: string; name: string; company_name: string; lead_status: string; priority: string; owner_name: string; idle_days: number }>;
  }>(`/api/Report/LeadAgeing${buildQuery({ head_id: headId, days })}`);

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <SectionCard title="Open leads by idle time" description="Days since the last activity on each lead">
          <DataTable
            columns={[
              { key: "lead_status", header: "Status", render: (r) => <span className="font-medium text-foreground">{r.lead_status}</span> },
              { key: "d0_3", header: "0–3 days", align: "right" },
              { key: "d4_7", header: "4–7 days", align: "right" },
              { key: "d8_14", header: "8–14 days", align: "right" },
              { key: "d15_30", header: "15–30 days", align: "right" },
              { key: "d30_plus", header: "30+ days", align: "right" },
              { key: "total", header: "Total", align: "right", render: (r) => <span className="font-semibold text-foreground">{r.total}</span> },
            ]}
            rows={data?.rows || []}
            rowKey={(r) => r.lead_status}
          />
        </SectionCard>
        <SectionCard
          title="Stuck leads"
          description={`Open leads with no activity for more than ${days} days`}
          action={
            <select aria-label="Stale threshold" className={filterSelectClass} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[3, 7, 14, 30].map((d) => (
                <option key={d} value={d}>
                  {d}+ days
                </option>
              ))}
            </select>
          }
        >
          <DataTable
            columns={[
              { key: "name", header: "Lead", render: (r) => <span className="font-medium text-foreground">{r.name || r.company_name}</span> },
              { key: "company_name", header: "Company" },
              { key: "lead_status", header: "Status" },
              { key: "priority", header: "Priority" },
              { key: "owner_name", header: "Owner" },
              { key: "idle_days", header: "Idle days", align: "right" },
            ]}
            rows={data?.stuck_leads || []}
            rowKey={(r) => r.id}
            defaultSort={{ key: "idle_days", dir: "desc" }}
            emptyMessage="No stuck leads."
          />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

export function ActivityTab({ range, headId }: TabProps) {
  const { data, loading, error } = useReport<{
    rows: Array<{ user_id: string; name: string; role: string; calls: number; meetings: number; emails: number; whatsapp: number; notes: number; total_activities: number; tasks_done: number; tasks_overdue: number }>;
    daily: Array<{ day: string; activities: number }>;
  }>(`/api/Report/Activity${q(range, headId)}`);
  const daily = data?.daily || [];
  const rows = data?.rows || [];

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <SectionCard title="Activities per day" description="Daily velocity of calls, meetings, emails, WhatsApp and notes">
          <AreaTrendChart
            data={daily.map((d) => ({
              period: d.day,
              label: new Date(`${d.day}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
              value: d.activities,
            }))}
            formatValue={(v) => `${v} activities`}
            color="#06b6d4"
            height={210}
          />
        </SectionCard>

        {rows.length > 0 && (
          <SectionCard title="Activity breakdown by person" description="Calls vs meetings vs emails vs WhatsApp logged">
            <GroupedBarChart
              data={rows.slice(0, 10).map((r) => ({
                category: r.name,
                calls: r.calls,
                meetings: r.meetings,
                emails: r.emails,
                whatsapp: r.whatsapp,
              }))}
              series={[
                { key: "calls", name: "Calls", color: "#3b82f6" },
                { key: "meetings", name: "Meetings", color: "#8b5cf6" },
                { key: "emails", name: "Emails", color: "#06b6d4" },
                { key: "whatsapp", name: "WhatsApp", color: "#10b981" },
              ]}
              height={210}
            />
          </SectionCard>
        )}

        <SectionCard title="Activity by person">
          <DataTable
            columns={[
              { key: "name", header: "Name", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
              { key: "role", header: "Role" },
              { key: "calls", header: "Calls", align: "right" },
              { key: "meetings", header: "Meetings", align: "right" },
              { key: "emails", header: "Emails", align: "right" },
              { key: "whatsapp", header: "WhatsApp", align: "right" },
              { key: "notes", header: "Notes", align: "right" },
              { key: "total_activities", header: "Total", align: "right" },
              { key: "tasks_done", header: "Tasks done", align: "right" },
              { key: "tasks_overdue", header: "Overdue now", align: "right" },
            ]}
            rows={rows}
            rowKey={(r) => r.user_id}
            defaultSort={{ key: "total_activities", dir: "desc" }}
          />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

export function QuotesTab({ range, headId }: TabProps) {
  const { data, loading, error } = useReport<{
    rows: Array<{ user_id: string; name: string; role: string; created: number; submitted: number; approved: number; sent: number; accepted: number; rejected: number; pending_now: number; acceptance_pct: number; accepted_value: number; avg_discount_pct: number; avg_approval_hours: number }>;
  }>(`/api/Report/Quotes${q(range, headId)}`);
  const rows = data?.rows || [];

  const funnelTotals = React.useMemo(() => {
    let created = 0, submitted = 0, approved = 0, sent = 0, accepted = 0, rejected = 0, acceptedVal = 0;
    rows.forEach((r) => {
      created += r.created || 0;
      submitted += r.submitted || 0;
      approved += r.approved || 0;
      sent += r.sent || 0;
      accepted += r.accepted || 0;
      rejected += r.rejected || 0;
      acceptedVal += r.accepted_value || 0;
    });
    return { created, submitted, approved, sent, accepted, rejected, acceptedVal };
  }, [rows]);

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        {rows.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Quote conversion funnel" description="Created to accepted quote progression">
              <FunnelConversionChart
                stages={[
                  { name: "Created", count: funnelTotals.created },
                  { name: "Submitted", count: funnelTotals.submitted },
                  { name: "Approved", count: funnelTotals.approved },
                  { name: "Sent", count: funnelTotals.sent },
                  { name: "Accepted", count: funnelTotals.accepted },
                ]}
              />
            </SectionCard>

            <SectionCard title="Quote outcome distribution" description="Accepted vs rejected vs in progress">
              <DonutPieChart
                data={[
                  { label: "Accepted", value: funnelTotals.accepted, color: "#10b981", sublabel: fmtMoney(funnelTotals.acceptedVal) },
                  { label: "In Progress", value: Math.max(0, funnelTotals.sent - funnelTotals.accepted - funnelTotals.rejected), color: "#3b82f6" },
                  { label: "Rejected", value: funnelTotals.rejected, color: "#f43f5e" },
                ].filter((d) => d.value > 0)}
                donut={true}
                centerValue={String(funnelTotals.created)}
                centerLabel="Total Quotes"
              />
            </SectionCard>
          </div>
        )}

        <SectionCard title="Quotes by owner" description="Quote funnel, discounts and approval time in the period">
          <DataTable
            columns={[
              { key: "name", header: "Owner", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
              { key: "created", header: "Created", align: "right" },
              { key: "submitted", header: "Submitted", align: "right" },
              { key: "approved", header: "Approved", align: "right" },
              { key: "sent", header: "Sent", align: "right" },
              { key: "accepted", header: "Accepted", align: "right" },
              { key: "rejected", header: "Rejected", align: "right" },
              { key: "acceptance_pct", header: "Accept %", align: "right", render: (r) => fmtPct(r.acceptance_pct) },
              { key: "accepted_value", header: "Accepted value", align: "right", render: (r) => fmtMoney(r.accepted_value) },
              { key: "avg_discount_pct", header: "Avg disc.", align: "right", render: (r) => fmtPct(r.avg_discount_pct) },
              { key: "avg_approval_hours", header: "Avg approval (h)", align: "right" },
              { key: "pending_now", header: "Pending now", align: "right" },
            ]}
            rows={rows}
            rowKey={(r) => r.user_id}
            defaultSort={{ key: "accepted_value", dir: "desc" }}
            emptyMessage="No quote activity in this period."
          />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Forecast
// ---------------------------------------------------------------------------

export function ForecastTab({ headId }: TabProps) {
  const { data, loading, error } = useReport<{
    overdue: ForecastBucket;
    this_month: ForecastBucket;
    next_month: ForecastBucket;
    this_quarter: ForecastBucket;
    undated_deals: number;
    by_owner: Array<{ user_id: string; name: string; deals: number; amount: number; weighted: number }>;
    closing_deals: Array<{ id: string; name: string; amount: number; weighted_amount: number; probability: number; expected_close_date: string; stage_name: string; owner_name: string }>;
  }>(`/api/Report/Forecast${buildQuery({ head_id: headId })}`);

  const tile = (label: string, b?: ForecastBucket) => (
    <StatTile label={label} value={fmtMoney(b?.weighted || 0)} hint={<span>{b?.deals || 0} deals · {fmtMoney(b?.amount || 0)} total</span>} />
  );

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {tile("This month (weighted)", data?.this_month)}
          {tile("Next month (weighted)", data?.next_month)}
          {tile("This quarter (weighted)", data?.this_quarter)}
          {tile("Past expected date", data?.overdue)}
        </div>
        {!!data?.undated_deals && (
          <p className="text-xs text-muted-foreground">{data.undated_deals} open deals have no expected close date and are not in the forecast.</p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Forecast by period" description="Weighted vs total pipeline nominal value">
            <GroupedBarChart
              data={[
                { category: "This Month", weighted: data?.this_month?.weighted || 0, nominal: data?.this_month?.amount || 0 },
                { category: "Next Month", weighted: data?.next_month?.weighted || 0, nominal: data?.next_month?.amount || 0 },
                { category: "This Qtr", weighted: data?.this_quarter?.weighted || 0, nominal: data?.this_quarter?.amount || 0 },
                { category: "Overdue", weighted: data?.overdue?.weighted || 0, nominal: data?.overdue?.amount || 0 },
              ]}
              series={[
                { key: "weighted", name: "Weighted Forecast", color: "#10b981" },
                { key: "nominal", name: "Total Pipeline", color: "#3b82f6" },
              ]}
              formatValue={(v) => fmtMoney(v)}
              height={220}
            />
          </SectionCard>

          <SectionCard title="This month by owner" description="Weighted value distribution">
            <DonutPieChart
              data={(data?.by_owner || []).filter((o) => o.weighted > 0).map((o) => ({
                label: o.name,
                value: o.weighted,
                sublabel: `${o.deals} deals`,
              }))}
              formatValue={(v) => fmtMoney(v)}
              centerValue={fmtMoney(data?.this_month?.weighted || 0)}
              centerLabel="Weighted"
              donut={true}
            />
          </SectionCard>
        </div>

        <SectionCard title="Deals closing this month" description="Including deals past their expected date">
          <DataTable
            columns={[
              { key: "name", header: "Deal", render: (r) => <Link to={`/deals/${r.id}`} className="font-medium text-primary hover:underline">{r.name}</Link> },
              { key: "owner_name", header: "Owner" },
              { key: "stage_name", header: "Stage" },
              { key: "expected_close_date", header: "Expected", render: (r) => fmtDate(r.expected_close_date) },
              { key: "probability", header: "Prob.", align: "right", render: (r) => fmtPct(r.probability) },
              { key: "amount", header: "Amount", align: "right", render: (r) => fmtMoney(r.amount) },
            ]}
            rows={data?.closing_deals || []}
            rowKey={(r) => r.id}
            emptyMessage="Nothing expected to close this month."
          />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Product sales
// ---------------------------------------------------------------------------

export function ProductsTab({ range, headId }: TabProps) {
  const { data, loading, error } = useReport<{
    rows: Array<{ product_id: string; product_name: string; product_code: string; deals: number; quantity: number; revenue: number; cost?: number; margin?: number; margin_pct?: number }>;
  }>(`/api/Report/ProductSales${q(range, headId)}`);
  const rows = data?.rows || [];
  // cost / margin are removed by the server for users without cost visibility
  const hasCost = rows.some((r) => r.margin !== undefined);

  const productChartData = React.useMemo(() => {
    const sorted = [...rows].filter((r) => r.revenue > 0).sort((a, b) => b.revenue - a.revenue);
    const top = sorted.slice(0, 7).map((r) => ({
      label: r.product_name,
      value: r.revenue,
      sublabel: `${fmtNum(r.quantity)} units`,
    }));
    const remainder = sorted.slice(7).reduce((sum, r) => sum + r.revenue, 0);
    if (remainder > 0) {
      top.push({ label: "Other Products", value: remainder, sublabel: `${sorted.length - 7} products` });
    }
    return top;
  }, [rows]);

  const columns: Column<(typeof rows)[number]>[] = [
    { key: "product_name", header: "Product", render: (r) => <span className="font-medium text-foreground">{r.product_name}</span> },
    { key: "product_code", header: "Code", render: (r) => r.product_code || "—" },
    { key: "deals", header: "Deals", align: "right" },
    { key: "quantity", header: "Qty", align: "right", render: (r) => fmtNum(r.quantity) },
    { key: "revenue", header: "Revenue", align: "right", render: (r) => fmtMoney(r.revenue) },
    ...(hasCost
      ? ([
          { key: "cost", header: "Cost", align: "right", render: (r) => fmtMoney(r.cost || 0) },
          { key: "margin", header: "Margin", align: "right", render: (r) => fmtMoney(r.margin || 0) },
          { key: "margin_pct", header: "Margin %", align: "right", render: (r) => fmtPct(r.margin_pct || 0) },
        ] as Column<(typeof rows)[number]>[])
      : []),
  ];

  return (
    <Status loading={loading} error={error}>
      <div className="space-y-4">
        {productChartData.length > 0 && (
          <SectionCard title="Revenue share by product" description="Won deals product revenue contribution">
            <DonutPieChart
              data={productChartData}
              formatValue={(v) => fmtMoney(v)}
              centerValue={fmtMoney(rows.reduce((sum, r) => sum + r.revenue, 0))}
              centerLabel="Total Revenue"
              donut={true}
            />
          </SectionCard>
        )}
        <SectionCard title="Product details">
          <DataTable columns={columns} rows={rows} rowKey={(r) => r.product_id || r.product_name} defaultSort={{ key: "revenue", dir: "desc" }} emptyMessage="No won deals with products in this period." />
        </SectionCard>
      </div>
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Assignment audit
// ---------------------------------------------------------------------------

export function AssignmentsTab({ range, headId }: TabProps) {
  const rangeKey = `${range.from}|${range.to}|${headId}`;
  const [pageState, setPageState] = useState({ key: rangeKey, page: 1 });
  const page = pageState.key === rangeKey ? pageState.page : 1;
  const { data, loading, error } = useReport<{
    rows: Array<{ id: string; created_on: string; reason: string; lead_name: string; company_name: string; from_user: string; to_user: string; assigned_by: string }>;
    total: number;
    page: number;
    page_size: number;
  }>(`/api/Report/AssignmentAudit${q(range, headId, { page, page_size: 30 })}`);
  const rows = data?.rows || [];
  const total = data?.total ?? rows.length;
  const pageSize = data?.page_size || 30;
  return (
    <Status loading={loading} error={error}>
      <SectionCard title="Lead assignments" description="Auto-assignments, reassignments and pool claims">
        {rows.length === 0 ? (
          <EmptyState message="No assignments in this period." />
        ) : (
          <>
            <DataTable
              columns={[
                { key: "created_on", header: "When", sortValue: (r) => r.created_on, render: (r) => fmtDate(r.created_on) },
                { key: "lead_name", header: "Lead", render: (r) => <span className="font-medium text-foreground">{r.lead_name || r.company_name}</span> },
                { key: "from_user", header: "From", render: (r) => r.from_user || "Pool / new" },
                { key: "to_user", header: "To", render: (r) => r.to_user || "—" },
                { key: "assigned_by", header: "By", render: (r) => r.assigned_by || "—" },
                { key: "reason", header: "Reason", render: (r) => r.reason.replace(/_/g, " ").toLowerCase() },
              ]}
              rows={rows}
              rowKey={(r) => r.id}
              defaultSort={{ key: "created_on", dir: "desc" }}
            />
            <ListPager page={page} pageSize={pageSize} total={total} onPage={(next) => setPageState({ key: rangeKey, page: next })} disabled={loading} />
          </>
        )}
      </SectionCard>
    </Status>
  );
}
